import React, { useState, useEffect, useRef, useContext } from 'react';
import { Howl } from 'howler';
import "../../../../Assets/Css/player.css";
import Detail from "./Detail";
import Controls from "./Controls";
import Volume from "./Volume";
import PlaylistContext from "../../../Context/PlaylistContext";

const MusicPlayer = () => {
    const { playlist } = useContext(PlaylistContext);

    const [playing, setPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [progress, setProgress] = useState(0);
    const [currentTrack, setCurrentTrack] = useState(0);
    const [sound, setSound] = useState(null);
    const [seek, setSeek] = useState(0);
    const [seekChanged, setSeekChanged] = useState(false);
    const [lastSeek, setLastSeek] = useState(0);
    const [totalTime, setTotalTime] = useState('0:00');
    const [remainingTime, setRemainingTime] = useState('0:00');
    const [loading, setLoading] = useState(true);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const soundRef = useRef(null);

    useEffect(() => {
        setIsMobile(window.innerWidth < 768);
    }, [window.innerWidth < 768]);

    useEffect(() => {
        setCurrentTrack(0);
    }, [playlist]);

    useEffect(() => {
        const initializeSound = () => {
            setLoading(true);

            if (soundRef.current) {
                soundRef.current.stop();
            }

            if (seekChanged) {
                soundRef.current.seek(seek);
                setSeekChanged(false);
            } else {
                const newSound = new Howl({
                    src: [playlist[currentTrack].link],
                    html5: true,
                    onplay: () => {
                        setPlaying(true);
                        updateProgress();
                        setLoading(false);
                    },
                    onpause: () => {
                        setPlaying(false);
                    },
                    onend: () => {
                        setPlaying(false);
                        setProgress(0);
                        setRemainingTime('0:00');
                        playNextTrack();
                    },
                });

                setSound(newSound);
                soundRef.current = newSound;
                soundRef.current.play();
            }
        };

        initializeSound();
    }, [currentTrack, seek, playlist]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (soundRef.current && playing) {
                const currentDuration = soundRef.current.duration();
                const currentTime = soundRef.current.seek();
                const remaining = currentDuration - currentTime;
                setRemainingTime(formatTime(remaining));
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [playing, soundRef]);

    const playPauseToggle = () => {
        if (soundRef.current) {
            if (playing) {
                soundRef.current.pause();
            } else {
                soundRef.current.play();
            }
        }
    };

    const updateProgress = () => {
        if (soundRef.current) {
            const newProgress = (soundRef.current.seek() / soundRef.current.duration()) * 100;
            setProgress(newProgress);

            const currentDuration = soundRef.current.duration();
            const currentTime = soundRef.current.seek();
            const remaining = currentDuration - currentTime;

            setTotalTime(formatTime(currentDuration));
            setRemainingTime(formatTime(remaining));

            requestAnimationFrame(updateProgress);
        }
    };

    const formatTime = (timeInSeconds) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const setVolumeValue = (value) => {
        if (soundRef.current) {
            soundRef.current.volume(value);
            setVolume(value);
        }
    };

    const playNextTrack = () => {
        if (currentTrack < playlist.length - 1) {
            if (soundRef.current) {
                soundRef.current.stop();
            }

            setCurrentTrack(currentTrack + 1);

            const newSound = new Howl({
                src: [playlist[currentTrack + 1].link],
                html5: true,
                onplay: () => {
                    setPlaying(true);
                    updateProgress();
                    setLoading(false);
                },
                onpause: () => {
                    setPlaying(false);
                },
                onend: () => {
                    setPlaying(false);
                    setProgress(0);
                    setRemainingTime('0:00');
                    playNextTrack();
                },
            });

            setSound(newSound);
            soundRef.current = newSound;
            soundRef.current.play();
        }
    };

    const playPreviousTrack = () => {
        if (currentTrack > 0) {
            setCurrentTrack(currentTrack - 1);
        }
    };

    const changePlaybackTime = (newTime) => {
        if (soundRef.current) {
            const newSeek = (newTime / 100) * soundRef.current.duration();
            if (newSeek !== lastSeek) {
                soundRef.current.seek(newSeek);
                setSeek(newSeek);
                setLastSeek(newSeek);
                setSeekChanged(true);
            }
        }
    };

    const handleOpenFullScreenPlayerMobile = () => {
        isMobile && setIsFullScreen(!isFullScreen);
    };

    const handleOpenFullScreenPlayerDesktop = () => {
        setIsFullScreen(!isFullScreen);
    };

    const handleCloseFullScreenPlayer = () => {
        setIsFullScreen(false);
    };

    document.body.style.overflow = isFullScreen ? "hidden" : "auto";

    return (
        <div
            className=
                {`
                    ${isFullScreen && 'top-0 mx-0 !bottom-0 bg-gradient-to-r from-[#8B00FF] to-[#FF00FF] !rounded-none'}  
                    fixed transition-all left-0 right-0 bottom-0 z-20 w-auto md:w-full 
                    bg-violet-700 p-1  
                    flex justify-center 
                    text-white rounded-t-xl shadow-2xl shadow-purple-900
                `}
        >

            <div className={`
                    ${isFullScreen && 'flex-col max-w-[600px] self-center space-y-12'}  
                    ${(isFullScreen && isMobile) && '!flex-col !p-8 justify-around'}                  
                    flex flex-row justify-between items-center w-full
                    space-x-4 space-x-reverse
            `}>
                <Detail
                    song={playlist[currentTrack]}
                    isFullScreen={isFullScreen}
                    isMobile={isMobile}
                    handleOpenFullScreenPlayerMobile={handleOpenFullScreenPlayerMobile}
                />
                <Controls
                    isFullScreen={isFullScreen}
                    isMobile={isMobile}
                    progress={progress}
                    playing={playing}
                    totalTime={totalTime}
                    remainingTime={remainingTime}
                    changePlaybackTime={changePlaybackTime}
                    playPreviousTrack={playPreviousTrack}
                    playPauseToggle={playPauseToggle}
                    playNextTrack={playNextTrack}
                />
                <Volume
                    isFullScreen={isFullScreen}
                    isMobile={isMobile}
                    volume={volume}
                    setVolumeValue={setVolumeValue}
                    handleCloseFullScreenPlayer={handleCloseFullScreenPlayer}
                    handleOpenFullScreenPlayerDesktop={handleOpenFullScreenPlayerDesktop}
                />
            </div>
        </div>
    );
};

export default MusicPlayer;

